import moment from "moment/moment";
import React from "react";

import { Button2 } from "@/ui2/atoms/Button";
import { createClassNameString } from "@/utils/createClassNameString";
import { getAutomationDataAttr } from "@/utils/automationUtils";
import { Menu2 } from "@/components/UI2/molecules/Menu";
import { Text } from "@/component-library/Text";
import { TextColors, TextTypes } from "@/component-library/constants";

const OBJECTIVE_MENU_ITEMS = [
  { id: "edit", label: "Edit Target Date" },
  { id: "complete", label: "Achieve Objective" },
  { id: "remove", label: "Remove Objective" },
];

const ActiveObjective = ({
  canEdit,
  fieldsToHide = [],
  goal,
  hideObjectiveActions,
  objective,
  onCompleteObjectiveClick,
  onEditObjectiveClick,
  onRemoveObjectiveClick,
}) => {
  const isPastTargetDate = moment(objective.objective_target_timestamp).isBefore(moment());

  const objectiveText = objective?.objective_other_text || objective.objective_text;

  return (
    <div
      className="CareGoalsCard--objective-list-item"
      {...getAutomationDataAttr(
        `ActiveObjective_${goal.goal_text_id}_${objective.objective_text_id}`,
      )}
    >
      <div className="CareGoalsCard--objective-list-text-col">
        <Text>{objectiveText}</Text>
      </div>

      <div className="CareGoalsCard--objective-list-meta-data-cols">
        {!fieldsToHide.includes("objective_start_timestamp") && (
          <div className="CareGoalsCard--objective-list-start-date-col">
            <Text>{moment(objective.objective_start_timestamp).format("MM/DD/YY")}</Text>
          </div>
        )}

        <div className="CareGoalsCard--objective-list-target-date-col">
          <Text color={isPastTargetDate ? TextColors.yellow : TextColors.black}>
            {moment(objective.objective_target_timestamp).format("MM/DD/YY")}
          </Text>
        </div>

        {!hideObjectiveActions && (
          <div className="CareGoalsCard--objective-list-more-col">
            <Menu2
              isDisabled={!canEdit}
              items={OBJECTIVE_MENU_ITEMS}
              onAction={(key) => {
                switch (key) {
                  case "edit":
                    onEditObjectiveClick(goal, objective);
                    break;

                  case "complete":
                    onCompleteObjectiveClick(goal, objective);
                    break;

                  case "remove":
                    onRemoveObjectiveClick(goal, objective);
                    break;
                }
              }}
              placement="bottom end"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export function ActiveGoal({
  canEdit,
  className,
  fieldsToHide = [],
  goal,
  hideObjectiveActions,
  onAddObjectiveClick,
  onCompleteObjectiveClick,
  onEditObjectiveClick,
  onRemoveObjectiveClick,
}) {
  const objectives = goal.objectives || [];
  const goalText = goal?.goal_other_text || goal.goal_text;
  const shouldShowAddObjective = !hideObjectiveActions && canEdit && objectives.length < 3;

  return (
    <div
      className={createClassNameString(["CareGoalsCard--active-goal", className])}
      key={goal.goal_id}
      {...getAutomationDataAttr(`ActiveGoal_${goal.goal_text_id}`)}
    >
      <div className="CareGoalsCard--active-goal-text">
        <Text type={TextTypes.label}>{goalText}</Text>
      </div>

      <div className="CareGoalsCard--objective-list">
        <div className="CareGoalsCard--objective-list-header">
          <div className="CareGoalsCard--objective-list-text-col">
            <Text type={TextTypes.light}>Objective</Text>
          </div>

          <div className="CareGoalsCard--objective-list-meta-data-cols">
            {!fieldsToHide.includes("objective_start_timestamp") && (
              <div className="CareGoalsCard--objective-list-start-date-col">
                <Text type={TextTypes.light}>Start Date</Text>
              </div>
            )}

            <div className="CareGoalsCard--objective-list-target-date-col">
              <Text type={TextTypes.light}>Target Date</Text>
            </div>

            {!hideObjectiveActions && (
              <div className="CareGoalsCard--objective-list-more-col"></div>
            )}
          </div>
        </div>

        {objectives.map((objective) => {
          return (
            <ActiveObjective
              canEdit={canEdit}
              fieldsToHide={fieldsToHide}
              goal={goal}
              hideObjectiveActions={hideObjectiveActions}
              key={`ActiveObjective-${goal.goal_id}.${objective.objective_id}`}
              objective={objective}
              onCompleteObjectiveClick={onCompleteObjectiveClick}
              onEditObjectiveClick={onEditObjectiveClick}
              onRemoveObjectiveClick={onRemoveObjectiveClick}
            />
          );
        })}
      </div>

      {shouldShowAddObjective && (
        <div className="tw:flex tw:items-center tw:gap-ch">
          <Button2 onPress={() => onAddObjectiveClick(goal)} size="xs" variant="link">
            Add objective
          </Button2>

          <small className="body-xs">(Max 3)</small>
        </div>
      )}
    </div>
  );
}
